/**
 * Derive a full desktop-sidebar palette from one user-picked colour
 * (Settings → Appearance → Sidebar → Custom). Returns the CSS custom
 * properties that appShell.css presets define, so a custom colour behaves
 * exactly like a preset: text contrast flips for light bases, hover/border
 * shades are mixed from the base toward the foreground.
 */

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function normalizeHex(value) {
  const m = HEX_RE.exec(String(value || '').trim());
  if (!m) return null;
  let hex = m[1].toLowerCase();
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  return `#${hex}`;
}

function toRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]) {
  return `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;
}

function mix(a, b, t) {
  return a.map((v, i) => v + (b[i] - v) * t);
}

// WCAG relative luminance
function luminance([r, g, b]) {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

// Above this luminance dark text out-contrasts white text (WCAG crossover).
const LIGHT_THRESHOLD = 0.179;

export function isLightColor(hex) {
  const n = normalizeHex(hex);
  return n ? luminance(toRgb(n)) > LIGHT_THRESHOLD : false;
}

/** CSS variables for a custom sidebar base colour (null when invalid). */
export function deriveSidebarPalette(value) {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const base = toRgb(hex);
  const light = luminance(base) > LIGHT_THRESHOLD;
  const ink = light ? [15, 15, 17] : [255, 255, 255];
  const fg = mix(base, ink, light ? 0.78 : 0.84);
  return {
    '--sidebar-background': hex,
    '--sidebar-foreground': toHex(fg),
    '--sidebar-muted': toHex(mix(base, ink, light ? 0.52 : 0.5)),
    '--sidebar-strong': toHex(ink),
    '--sidebar-primary': light ? '#E56E00' : '#FF7A00',
    '--sidebar-ring': light ? '#E56E00' : '#FF7A00',
    '--sidebar-accent': toHex(mix(base, ink, light ? 0.07 : 0.09)),
    '--sidebar-accent-foreground': toHex(ink),
    '--sidebar-border': toHex(mix(base, ink, light ? 0.1 : 0.12)),
    '--sidebar-surface': light ? 'rgba(15, 15, 17, 0.03)' : 'rgba(255, 255, 255, 0.04)',
    '--sidebar-surface-2': light ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.06)',
    '--sidebar-scrollbar': toHex(mix(base, ink, 0.18)),
    '--sidebar-badge-bg': light ? 'rgba(255, 122, 0, 0.12)' : 'rgba(255, 122, 0, 0.16)',
    '--sidebar-badge-fg': light ? '#C2410C' : '#FFA34D',
    // light rails need a hairline so the content card still reads
    '--shell-card-border': light ? toHex(mix(base, [15, 15, 17], 0.1)) : '',
  };
}

/** Swatch colours for the settings preview. */
export function sidebarPreviewFor(value) {
  const vars = deriveSidebarPalette(value);
  if (!vars) return null;
  return {
    bg: vars['--sidebar-background'],
    line: vars['--sidebar-scrollbar'],
    active: vars['--sidebar-accent'],
    text: vars['--sidebar-foreground'],
  };
}

export const SIDEBAR_CUSTOM_VAR_NAMES = Object.keys(deriveSidebarPalette('#000000'));

/* ------------------------------------------------------------------ */
/* Main panel (content card) colour — Settings → Appearance → Main panel */
/* ------------------------------------------------------------------ */

/**
 * Preset panel colours. `tone` ties a preset to a mode: light presets are
 * offered in light mode, dark ones in dark mode (a cream card in dark mode
 * would fight the ink text tokens). Token values live in appShell.css under
 * html[data-panel-theme]; `bg` only drives the swatches and the pre-paint
 * script.
 */
export const PANEL_THEMES = [
  { id: 'white', tone: 'light', name: 'White', description: 'Default', bg: '#FFFFFF' },
  { id: 'porcelain', tone: 'light', name: 'Porcelain', description: 'Cool soft white', bg: '#FAFAFC' },
  { id: 'linen', tone: 'light', name: 'Linen', description: 'Warm off-white', bg: '#FBF8F3' },
  { id: 'mist', tone: 'light', name: 'Mist', description: 'Cool blue-grey', bg: '#F6F8FB' },
  { id: 'sage', tone: 'light', name: 'Sage', description: 'Pale green-grey', bg: '#F5F8F5' },
  { id: 'onyx', tone: 'dark', name: 'Onyx', description: 'Default', bg: '#121216' },
  { id: 'graphite', tone: 'dark', name: 'Graphite', description: 'Softer charcoal', bg: '#1B1C21' },
  { id: 'midnight', tone: 'dark', name: 'Midnight', description: 'Deep navy', bg: '#10162A' },
  { id: 'espresso', tone: 'dark', name: 'Espresso', description: 'Warm dark brown', bg: '#1A1410' },
];
export const DEFAULT_PANEL_THEME = { light: 'white', dark: 'onyx' };
export const DEFAULT_PANEL_CUSTOM_COLOR = { light: '#FAF7F2', dark: '#15171C' };

/** CSS variables for a custom panel colour (null when invalid). */
export function derivePanelVars(value) {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const base = toRgb(hex);
  const light = luminance(base) > LIGHT_THRESHOLD;
  return {
    '--shell-card': hex,
    '--shell-card-border': toHex(mix(base, light ? [15, 15, 17] : [255, 255, 255], light ? 0.1 : 0.12)),
  };
}
export const PANEL_CUSTOM_VAR_NAMES = Object.keys(derivePanelVars('#000000'));

/* ------------------------------------------------------------------ */
/* Data colours — a whole chart palette from one lead colour           */
/* ------------------------------------------------------------------ */

const WHITE = [255, 255, 255];
const DARK_CANVAS = [18, 18, 22];

/**
 * Series colours (the chartColorsFor shape) for a custom lead colour, per
 * mode. Support series stay neutral greys so the lead is the only hue —
 * the same convention as the shipped presets.
 */
export function deriveChartPalette(value) {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const base = toRgb(hex);
  const lead = toHex(base);
  const leadDark = luminance(base) < 0.25 ? toHex(mix(base, WHITE, 0.3)) : lead; // lift very dark leads on dark canvases
  return {
    light: { primary: lead, primaryLight: toHex(mix(base, WHITE, 0.4)), secondary: '#A0A0AE', tertiary: '#D0D0DA', grid: '#E8E8F0', axis: '#72727F', positive: '#12B76A', negative: '#F04438' },
    dark: { primary: leadDark, primaryLight: toHex(mix(toRgb(leadDark), WHITE, 0.35)), secondary: '#8888A0', tertiary: '#4A4A58', grid: '#2A2A33', axis: '#8888A0', positive: '#12B76A', negative: '#F04438' },
  };
}

/** bklit --chart-* overrides for a custom lead colour, for one mode. */
export function deriveChartVars(value, dark) {
  const pal = deriveChartPalette(value);
  if (!pal) return null;
  const lead = toRgb(dark ? pal.dark.primary : pal.light.primary);
  const towards = dark ? DARK_CANVAS : WHITE;
  const steps = dark ? [0.75, 0.58, 0.4, 0.2, 0] : [0.9, 0.75, 0.55, 0.3, 0];
  const vars = {
    '--chart-1': toHex(lead),
    '--chart-4': toHex(mix(lead, towards, dark ? 0.3 : 0.4)),
    '--chart-crosshair': toHex(lead),
    '--chart-scale-pattern-color': dark ? '#121216' : toHex(mix(lead, WHITE, 0.94)),
  };
  steps.forEach((t, i) => { vars[`--chart-scale-0${i + 1}`] = toHex(mix(lead, towards, t)); });
  return vars;
}
export const CHART_CUSTOM_VAR_NAMES = Object.keys(deriveChartVars('#000000', false));
